import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../../components/feature/AuthModal';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

interface SeasonalFlower {
  id: string;
  month: string;
  festival: string;
  flowers: string;
  recipients: string;
  image: string;
  description: string;
}

const seasonalFlowers: SeasonalFlower[] = [
  {
    id: 'cny',
    month: '1-2月',
    festival: '農曆新年',
    flowers: '蘭花、銀柳、紅色系盆花',
    recipients: '長輩、企業客戶',
    image: 'https://public.readdy.ai/ai/img_res/4f6947a3681001fae8b51954365ab7f8.jpg',
    description: '喜氣洋洋的新年花禮，為您的長輩與客戶送上最誠摯的祝福'
  },
  {
    id: 'valentine',
    month: '2月',
    festival: '西洋情人節',
    flowers: '經典紅玫瑰、法式永生花',
    recipients: '戀人、另一半',
    image: 'https://readdy.ai/api/search-image?query=Romantic%20red%20roses%20bouquet%20French%20style%20elegant%20arrangement%20with%20preserved%20flowers%20Valentine%20Day%20gift%20professional%20florist%20photography%20soft%20pink%20background%20luxury%20wrapping&width=400&height=400&seq=val002&orientation=squarish',
    description: '以經典紅玫瑰傳遞愛意，法式永生花讓愛情永恆綻放'
  },
  {
    id: 'mother',
    month: '5月',
    festival: '母親節',
    flowers: '康乃馨、繡球花、粉色系盆花',
    recipients: '母親、女性長輩',
    image: 'https://readdy.ai/api/search-image?query=Beautiful%20Mothers%20Day%20flower%20bouquet%20with%20pink%20carnations%20and%20hydrangeas%20elegant%20French%20style%20arrangement%20soft%20pastel%20colors%20professional%20florist%20photography%20clean%20light%20background&width=400&height=400&seq=mom003&orientation=squarish',
    description: '溫馨粉嫩的花禮，獻給最偉大的母親與女性長輩'
  },
  {
    id: 'graduation',
    month: '6月',
    festival: '畢業季',
    flowers: '向日葵、滿天星、個人化小花束',
    recipients: '畢業生、師長',
    image: 'https://readdy.ai/api/search-image?query=Cheerful%20graduation%20flower%20bouquet%20with%20bright%20sunflowers%20and%20baby%20breath%20celebratory%20arrangement%20yellow%20and%20white%20colors%20professional%20florist%20photography%20clean%20background&width=400&height=400&seq=grad004&orientation=squarish',
    description: '陽光燦爛的向日葵，祝福每位畢業生前程似錦'
  },
  {
    id: 'qixi',
    month: '8月',
    festival: '七夕情人節',
    flowers: '永生花盒、玫瑰花束',
    recipients: '戀人、心儀對象',
    image: 'https://readdy.ai/api/search-image?query=Romantic%20Qixi%20Valentine%20preserved%20flower%20box%20with%20roses%20elegant%20gift%20box%20design%20pink%20and%20red%20colors%20professional%20florist%20photography%20luxury%20presentation%20clean%20background&width=400&height=400&seq=qixi005&orientation=squarish',
    description: '東方情人節的浪漫花禮，永生花盒承載永恆的愛'
  },
  {
    id: 'christmas',
    month: '12月',
    festival: '聖誕節/跨年',
    flowers: '聖誕花圈、聖誕紅設計、紅白玫瑰',
    recipients: '朋友、愛人、居家佈置',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20Christmas%20floral%20wreath%20with%20red%20poinsettia%20and%20white%20roses%20festive%20holiday%20decoration%20pine%20branches%20professional%20florist%20photography%20clean%20white%20background&width=400&height=400&seq=xmas006&orientation=squarish',
    description: '節慶氛圍滿滿的聖誕花藝，為您的空間增添溫馨'
  },
  {
    id: 'anniversary',
    month: '全年',
    festival: '週年紀念/生日',
    flowers: '永生花束、客製化鮮花',
    recipients: '另一半、好友、自己',
    image: 'https://readdy.ai/api/search-image?query=Luxurious%20anniversary%20preserved%20flower%20bouquet%20elegant%20custom%20design%20mixed%20colors%20roses%20and%20dried%20flowers%20professional%20florist%20photography%20gift%20wrapping%20clean%20background&width=400&height=400&seq=anni007&orientation=squarish',
    description: '專屬客製化花禮，為每個重要時刻留下美好回憶'
  }
];

export default function SeasonalBookingPage() {
  const [selectedFestival, setSelectedFestival] = useState<SeasonalFlower | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { user, member } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    festival: '',
    flowerType: '',
    budget: '',
    deliveryDate: '',
    deliveryTime: '',
    recipientName: '',
    recipientPhone: '',
    deliveryAddress: '',
    cardMessage: '',
    specialRequest: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'cardMessage' || name === 'specialRequest') {
      if (value.length > 500) return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFestivalSelect = (festival: SeasonalFlower) => {
    setSelectedFestival(festival);
    setFormData(prev => ({
      ...prev,
      festival: festival.festival,
      flowerType: festival.flowers
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d69m6k5t1k61917cmb40', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          festival: '',
          flowerType: '',
          budget: '',
          deliveryDate: '',
          deliveryTime: '',
          recipientName: '',
          recipientPhone: '',
          deliveryAddress: '',
          cardMessage: '',
          specialRequest: ''
        });
        setSelectedFestival(null);
      } else {
        // Optional: handle non‑OK responses
        console.error('Server responded with status', response.status);
      }
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      <SeoJsonLd type="seasonal-booking" />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-100/50 via-pink-50/30 to-amber-50/50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-medium mb-4">
              <i className="ri-calendar-event-line mr-2"></i>
              節日花禮預約
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              重要節日<span className="text-teal-600">花禮預約</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
              提前預約，讓每個重要時刻都有 Monceau Fleurs 的法式花藝相伴
            </p>
            <a
              href="https://www.instagram.com/monceautaiwan/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-medium hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl cursor-pointer"
            >
              <i className="ri-instagram-line text-xl"></i>
              追蹤 IG 看更多作品
            </a>
          </div>

          {/* 會員優惠橫幅 */}
          {!user ? (
            <div className="max-w-4xl mx-auto mb-8">
              <div className="bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 rounded-2xl p-6 shadow-xl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex-1 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-vip-crown-line text-2xl"></i>
                      <h3 className="text-xl font-bold">會員專屬優惠</h3>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="flex items-center gap-2">
                        <i className="ri-gift-line"></i>
                        <span>新會員註冊立即贈送 <strong className="text-yellow-300">100 點</strong></span>
                      </p>
                      <p className="flex items-center gap-2">
                        <i className="ri-discount-percent-line"></i>
                        <span>節日預約享 <strong className="text-yellow-300">9 折優惠</strong></span>
                      </p>
                      <p className="flex items-center gap-2">
                        <i className="ri-coin-line"></i>
                        <span>訂單完成回饋 <strong className="text-yellow-300">10% 點數</strong></span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsAuthModalOpen(true)}
                    className="px-8 py-3 bg-white text-rose-600 rounded-full font-bold hover:bg-yellow-50 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                  >
                    立即加入會員
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto mb-8">
              <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-6 shadow-xl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex-1 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-vip-crown-line text-2xl"></i>
                      <h3 className="text-xl font-bold">會員專屬優惠進行中</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2">
                        <i className="ri-discount-percent-line text-xl"></i>
                        <div>
                          <p className="font-bold">9 折優惠</p>
                          <p className="text-xs opacity-90">節日預約專屬</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2">
                        <i className="ri-coin-line text-xl"></i>
                        <div>
                          <p className="font-bold">10% 點數回饋</p>
                          <p className="text-xs opacity-90">訂單完成後</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2">
                        <i className="ri-wallet-line text-xl"></i>
                        <div>
                          <p className="font-bold">{member?.points || 0} 點</p>
                          <p className="text-xs opacity-90">可用點數</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Festival Cards Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">節日花禮指南</h2>
            <p className="text-gray-600">點選節日查看推薦花材，並進行預約</p>
          </div>

          {/* 手機版：橫向滑動 */}
          <div className="md:hidden mb-8">
            <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
              <div className="flex gap-4" style={{ width: 'max-content' }}>
                {seasonalFlowers.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleFestivalSelect(item)}
                    className={`group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border-2 flex-shrink-0 ${
                      selectedFestival?.id === item.id ? 'border-teal-500 ring-2 ring-teal-200' : 'border-transparent'
                    }`}
                    style={{ width: '280px' }}
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.festival}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-gray-700">
                        {item.month}
                      </div>
                      {selectedFestival?.id === item.id && (
                        <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-teal-500 rounded-full">
                          <i className="ri-check-line text-white text-lg"></i>
                        </div>
                      )}
                      {user && (
                        <div className="absolute bottom-3 right-3 px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                          9折
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                        {item.festival}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <i className="ri-flower-line text-teal-500 mt-0.5"></i>
                          <span className="text-sm text-gray-700">{item.flowers}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <i className="ri-gift-line text-rose-500 mt-0.5"></i>
                          <span className="text-sm text-gray-700">{item.recipients}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">
              <i className="ri-arrow-left-right-line mr-1"></i>
              左右滑動查看更多節日
            </p>
          </div>

          {/* 桌面版：網格佈局 */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {seasonalFlowers.map((item) => (
              <div
                key={item.id}
                onClick={() => handleFestivalSelect(item)}
                className={`group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border-2 ${
                  selectedFestival?.id === item.id ? 'border-teal-500 ring-2 ring-teal-200' : 'border-transparent'
                }`}
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.festival}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-gray-700">
                    {item.month}
                  </div>
                  {selectedFestival?.id === item.id && (
                    <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-teal-500 rounded-full">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                  )}
                  {user && (
                    <div className="absolute bottom-3 right-3 px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                      9折
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                    {item.festival}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <i className="ri-flower-line text-teal-500 mt-0.5"></i>
                      <span className="text-sm text-gray-700">{item.flowers}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-gift-line text-rose-500 mt-0.5"></i>
                      <span className="text-sm text-gray-700">{item.recipients}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Instagram Gallery Link */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-4 p-6 bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl">
                <i className="ri-instagram-line text-3xl text-white"></i>
              </div>
              <div className="text-left">
                <h3 className="font-bold text-gray-900 mb-1">探索更多花藝作品</h3>
                <p className="text-sm text-gray-600 mb-2">追蹤我們的 Instagram 獲取最新靈感</p>
                <a
                  href="https://www.instagram.com/monceautaiwan/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-pink-600 hover:text-pink-700 cursor-pointer"
                >
                  @monceautaiwan
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section id="booking-form" className="py-16 bg-gradient-to-b from-white to-rose-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">預約花禮</h2>
            <p className="text-gray-600">填寫以下表單，我們將盡快與您聯繫確認訂單</p>
            {user && (
              <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-orange-50 text-orange-700 rounded-full text-sm font-medium">
                <i className="ri-vip-crown-line"></i>
                會員專屬：9 折優惠 + 10% 點數回饋
              </div>
            )}
          </div>

          {submitSuccess ? (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                <i className="ri-check-double-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">預約成功！</h3>
              <p className="text-gray-600 mb-6">
                感謝您的預約，我們將在 24 小時內與您聯繫確認訂單詳情。
              </p>
              {user && (
                <div className="bg-orange-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-center gap-2 text-orange-700 mb-2">
                    <i className="ri-gift-line text-xl"></i>
                    <span className="font-bold">會員優惠已套用</span>
                  </div>
                  <div className="text-sm text-orange-600 space-y-1">
                    <p>✓ 已享 9 折優惠</p>
                    <p>✓ 訂單完成後將回饋 10% 點數</p>
                  </div>
                </div>
              )}
              <button
                onClick={() => setSubmitSuccess(false)}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                繼續預約其他花禮
              </button>
            </div>
          ) : (
            <form
              id="seasonal-booking-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-lg p-5 sm:p-8"
            >
              {/* Selected Festival Display */}
              {selectedFestival && (
                <div className="mb-6 sm:mb-8 p-4 bg-teal-50 rounded-xl flex items-center gap-4">
                  <img
                    src={selectedFestival.image}
                    alt={selectedFestival.festival}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 truncate">{selectedFestival.festival}</h4>
                    <p className="text-sm text-gray-600 truncate">{selectedFestival.flowers}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFestival(null);
                      setFormData(prev => ({ ...prev, festival: '', flowerType: '' }));
                    }}
                    className="p-2 hover:bg-teal-100 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-gray-500"></i>
                  </button>
                </div>
              )}

              {/* Contact Information */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-user-line text-teal-600"></i>
                  訂購人資訊
                </h3>
                {!user && (
                  <div className="mb-5 p-4 bg-gradient-to-r from-orange-50 to-rose-50 rounded-xl border border-orange-200">
                    <div className="flex items-start gap-3 mb-3">
                      <i className="ri-vip-crown-line text-2xl text-orange-500 mt-0.5"></i>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 mb-1">登入會員享三重優惠</p>
                        <div className="space-y-1 text-sm text-gray-700">
                          <p className="flex items-center gap-2">
                            <i className="ri-gift-line text-orange-500"></i>
                            新會員註冊送 100 點
                          </p>
                          <p className="flex items-center gap-2">
                            <i className="ri-discount-percent-line text-orange-500"></i>
                            節日預約享 9 折優惠
                          </p>
                          <p className="flex items-center gap-2">
                            <i className="ri-coin-line text-orange-500"></i>
                            訂單完成回饋 10% 點數
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsAuthModalOpen(true)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-rose-600 transition-all shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap text-base"
                    >
                      立即登入/註冊
                    </button>
                  </div>
                )}
                {user && (
                  <div className="mb-5 p-3 bg-teal-50 rounded-lg flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 text-xl"></i>
                    <span className="text-sm text-teal-700 font-medium">
                      已登入會員，享 9 折優惠 + 10% 點數回饋
                    </span>
                  </div>
                )}
                <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      姓名 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name || member?.name || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm bg-gray-50 focus:bg-white"
                      placeholder="請輸入您的姓名"
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      聯絡電話 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      inputMode="tel"
                      value={formData.phone || member?.phone || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm bg-gray-50 focus:bg-white"
                      placeholder="請輸入聯絡電話"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      電子郵件 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      inputMode="email"
                      value={formData.email || member?.email || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm bg-gray-50 focus:bg-white"
                      placeholder="請輸入電子郵件"
                    />
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-flower-line text-teal-600"></i>
                  花禮資訊
                </h3>
                <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      節日/場合 <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="festival"
                        value={formData.festival}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm cursor-pointer appearance-none bg-gray-50 focus:bg-white pr-10"
                      >
                        <option value="">請選擇節日</option>
                        {seasonalFlowers.map(item => (
                          <option key={item.id} value={item.festival}>{item.festival}</option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      預算範圍 <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm cursor-pointer appearance-none bg-gray-50 focus:bg-white pr-10"
                      >
                        <option value="">請選擇預算</option>
                        <option value="NT$1,000-2,000">NT$1,000-2,000</option>
                        <option value="NT$2,000-3,000">NT$2,000-3,000</option>
                        <option value="NT$3,000-5,000">NT$3,000-5,000</option>
                        <option value="NT$5,000-8,000">NT$5,000-8,000</option>
                        <option value="NT$8,000以上">NT$8,000以上</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xl"></i>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      偏好花材
                    </label>
                    <input
                      type="text"
                      name="flowerType"
                      value={formData.flowerType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm bg-gray-50 focus:bg-white"
                      placeholder="例如：玫瑰、繡球花、永生花..."
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-truck-line text-teal-600"></i>
                  配送資訊
                </h3>
                <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      希望送達日期 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="deliveryDate"
                      value={formData.deliveryDate}
                      onChange={handleInputChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm cursor-pointer bg-gray-50 focus:bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      希望送達時段 <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="deliveryTime"
                        value={formData.deliveryTime}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm cursor-pointer appearance-none bg-gray-50 focus:bg-white pr-10"
                      >
                        <option value="">請選擇時段</option>
                        <option value="上午 09:00-12:00">上午 09:00-12:00</option>
                        <option value="下午 12:00-15:00">下午 12:00-15:00</option>
                        <option value="下午 15:00-18:00">下午 15:00-18:00</option>
                        <option value="晚間 18:00-21:00">晚間 18:00-21:00</option>
                        <option value="門市自取">門市自取</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      收件人姓名 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="recipientName"
                      value={formData.recipientName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm bg-gray-50 focus:bg-white"
                      placeholder="請輸入收件人姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      收件人電話 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="recipientPhone"
                      inputMode="tel"
                      value={formData.recipientPhone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm bg-gray-50 focus:bg-white"
                      placeholder="請輸入收件人電話"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      配送地址 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="deliveryAddress"
                      value={formData.deliveryAddress}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm bg-gray-50 focus:bg-white"
                      placeholder="請輸入完整配送地址"
                    />
                    <p className="text-xs text-gray-400 mt-1">如選擇門市自取請填寫門市名稱</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-message-3-line text-teal-600"></i>
                  附加資訊
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      卡片祝福語
                    </label>
                    <textarea
                      name="cardMessage"
                      value={formData.cardMessage}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm resize-none bg-gray-50 focus:bg-white"
                      placeholder="請輸入您想寫在卡片上的祝福語（選填）"
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">500</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      特殊需求
                    </label>
                    <textarea
                      name="specialRequest"
                      value={formData.specialRequest}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-base sm:text-sm resize-none bg-gray-50 focus:bg-white"
                      placeholder="如有特殊需求或備註請在此說明（選填）"
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">500</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 sm:py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-bold text-lg hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    提交預約{user && ' (享 9 折優惠)'}
                  </>
                )}
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                提交後我們將在 24 小時內與您聯繫確認訂單
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Store Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">門市據點</h2>
            <p className="text-gray-600">歡迎親臨門市選購或自取</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                  <i className="ri-store-2-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">新光三越台北南西店</h3>
                  <a href="tel:02-2541-9859" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                    02-2541-9859
                  </a>
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-rose-500"></i>
                  10491臺北市中山區南京西路12號B2
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-time-line text-rose-500"></i>
                  營業時間：11:00–21:30
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                  <i className="ri-store-2-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">南港店</h3>
                  <a href="tel:02-2654-7976" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                    02-2654-7976
                  </a>
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-rose-500"></i>
                  115臺北市南港區經貿二路131號1樓
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-time-line text-rose-500"></i>
                  營業時間：11:00–21:30
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </div>
  );
}
